import type { SupplierInvoiceSyncByInvoiceNumbersResponse } from '@/ar/network/AccountsReceivable.network';

const AR_RUTTER_CONFIGURATION_TYPE = 'arRutter';

type ManualInvoiceSyncConfiguration = {
    id?: string,
    ownerID?: string,
    type?: string,
    isEnabled?: boolean,
    data?: {
        platform?: string,
        rutterAccessToken?: string,
        rutterConnectionID?: string,
    },
};

export function isManualInvoiceSyncConfigValid(
    configuration: ManualInvoiceSyncConfiguration | null | undefined,
    isConnectionReady: boolean = true,
): boolean {
    if (!configuration || configuration?.type !== AR_RUTTER_CONFIGURATION_TYPE || !configuration?.isEnabled || !isConnectionReady) {
        return false;
    }
    if (!configuration?.data) {
        return false;
    }

    const rutterData = configuration.data;

    return Boolean(
        configuration?.id
        && configuration?.ownerID
        && rutterData?.platform
        && rutterData?.rutterAccessToken
        && rutterData?.rutterConnectionID,
    );
}

export function isInvoiceSyncNotFoundErrorMessage(message: string): boolean {
    const lower = message.toLowerCase();
    return lower.includes('invoice_not_found') || lower.includes('not found') || lower.includes('no invoice');
}

export function shouldCloseManualInvoiceSyncModal(
    submittedInvoiceNumbers: string[],
    data: SupplierInvoiceSyncByInvoiceNumbersResponse,
): boolean {
    return submittedInvoiceNumbers.every((num) => data.invoices_synced.some((s) => s.invoice_number === num));
}

export function manualInvoiceSyncFailureToastMessage(
    submittedInvoiceNumbers: string[],
    data: SupplierInvoiceSyncByInvoiceNumbersResponse,
): string {
    const failures = submittedInvoiceNumbers
        .map((num) => data.invoices_failed.find((f) => f.invoice_number === num))
        .filter((f): f is { invoice_number: string, error: string } => f != null);
    const firstError = failures[0]?.error ?? 'Sync failed';
    if (failures.length > 0 && failures.every((f) => isInvoiceSyncNotFoundErrorMessage(f.error))) {
        return submittedInvoiceNumbers.length === 1
            ? 'No invoice found for that document number'
            : 'No invoices found for those document numbers';
    }
    return firstError;
}

/**
 * One document number per line; blank lines ignored; duplicates removed (first occurrence wins).
 */
export function parseInvoiceDocumentNumbers(raw: string): string[] {
    const lines = raw.split(/\r?\n/);
    const seen = new Set<string>();
    return lines
        .map((line) => line.trim())
        .filter((trimmed): trimmed is string => Boolean(trimmed))
        .filter((trimmed) => {
            if (seen.has(trimmed)) {
                return false;
            }
            seen.add(trimmed);
            return true;
        });
}
